import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  Building2,
  Target,
  GitBranch,
  Settings,
  LogOut,
  Plane,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  CreditCard
} from 'lucide-react';
import { FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const NAV_ITEMS = {
  super_admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Building2, label: 'Companies', path: '/dashboard/companies' },
    { icon: FileText, label: 'System Logs', path: '/dashboard/system-logs' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  company_admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Target, label: 'Leads', path: '/dashboard/leads' },
    { icon: GitBranch, label: 'Pipeline', path: '/dashboard/pipeline' },
    { icon: Clock, label: 'Follow-ups', path: '/dashboard/followups' },
    { icon: FileSpreadsheet, label: 'Reports', path: '/dashboard/reports' },
    { icon: Users, label: 'Users', path: '/dashboard/users' },
    { icon: CreditCard, label: 'Credits', path: '/dashboard/billing' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  manager: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Target, label: 'Leads', path: '/dashboard/leads' },
    { icon: GitBranch, label: 'Pipeline', path: '/dashboard/pipeline' },
    { icon: Clock, label: 'Follow-ups', path: '/dashboard/followups' },
    { icon: FileSpreadsheet, label: 'Reports', path: '/dashboard/reports' },
    { icon: Users, label: 'Team', path: '/dashboard/users' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  agent: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Target, label: 'My Leads', path: '/dashboard/leads' },
    { icon: GitBranch, label: 'Pipeline', path: '/dashboard/pipeline' },
    { icon: Clock, label: 'Follow-ups', path: '/dashboard/followups' },
    { icon: FileSpreadsheet, label: 'Reports', path: '/dashboard/reports' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = NAV_ITEMS[user?.role] || NAV_ITEMS.agent;

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "fixed left-0 top-0 h-full border-r border-border bg-card/95 backdrop-blur-sm z-40 flex flex-col transition-all duration-300",
          collapsed ? "w-16" : "w-60"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={cn("flex items-center h-16 px-4 border-b border-border", collapsed ? "justify-center" : "gap-2.5")}>
          <div className="w-8 h-8 rounded-lg bg-voyage-blue flex items-center justify-center flex-shrink-0">
            <Plane className="w-4 h-4 text-white" />
          </div>
          {!collapsed && (
            <span className="font-heading text-lg font-bold tracking-tight text-foreground">
              VoyageOS
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = isActive(item.path);
            const NavButton = (
              <Button
                key={item.path}
                variant="ghost"
                onClick={() => navigate(item.path)}
                className={cn(
                  "w-full justify-start gap-3 h-10 transition-all duration-200",
                  collapsed && "justify-center px-0",
                  active
                    ? "bg-voyage-blue/10 text-voyage-blue font-semibold hover:bg-voyage-blue/15 border-l-2 border-voyage-blue"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className={cn("w-4.5 h-4.5 flex-shrink-0", active && "text-voyage-blue")} />
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </Button>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>{NavButton}</TooltipTrigger>
                  <TooltipContent side="right" className="font-body">{item.label}</TooltipContent>
                </Tooltip>
              );
            }
            return NavButton;
          })}
        </nav>

        <Separator />

        {/* User & Logout */}
        <div className="p-3 space-y-2">
          {!collapsed && (
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          )}
          <Button
            variant="ghost"
            onClick={logout}
            className={cn(
              "w-full justify-start gap-3 h-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10",
              collapsed && "justify-center px-0"
            )}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span className="text-sm">Logout</span>}
          </Button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shadow-sm"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>
    </TooltipProvider>
  );
}
